import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { authApi } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { LogOut, ShieldAlert } from 'lucide-react';

export function LogoutDialog({ open, onOpenChange }) {
  const { logout } = useAuth();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      await authApi.verifyPassword(password);
      logout();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('auth.passwordLabel'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) setPassword('');
        onOpenChange(v);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mb-2">
            <ShieldAlert className="w-5 h-5 text-red-600" />
          </div>
          <DialogTitle className="text-[#0D1B2A]">{t('auth.logoutTitle')}</DialogTitle>
          <DialogDescription>
            {t('auth.logoutDesc')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="logout-pwd">{t('auth.passwordLabel')}</Label>
            <Input
              id="logout-pwd"
              data-testid={TID.logoutConfirmPwd}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="mt-1.5 h-11"
              required
            />
          </div>

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('auth.cancel')}
            </Button>
            <Button
              type="submit"
              data-testid={TID.logoutConfirmBtn}
              disabled={loading || !password}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              <LogOut className="w-4 h-4 mr-1.5" />
              {loading ? t('auth.verifying') : t('auth.confirmLogout')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
